import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import useTenant from '../hooks/useTenant';

export const ForgotPassword: React.FC = () => {
  const { tenantName, logoUrl } = useTenant();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please provide a valid email address.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      // Simulate recovery endpoint query
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setSubmitted(true);
    } catch (err: any) {
      setError(err?.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 shadow-xl p-8 transition-all hover:shadow-2xl">
        <div className="text-center mb-8">
          {logoUrl ? (
            <img src={logoUrl} alt={tenantName} className="w-14 h-14 mx-auto rounded-xl object-cover mb-4 shadow-sm border border-slate-100" />
          ) : (
            <div className="w-14 h-14 mx-auto rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-3xl mb-4">🏫</div>
          )}
          <h1 className="text-page-header mb-2">Reset Password</h1>
          <p className="text-helper">Enter your registered email address and we'll send you recovery details.</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg text-helper text-center flex items-center justify-center gap-2">
            <span>⚠️</span> {error}
          </div>
        )}

        {submitted ? (
          <div className="text-center space-y-6">
            <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto text-xl">✓</div>
            <div className="space-y-2">
              <h2 className="text-section-header text-emerald-800">Check Your Email</h2>
              <p className="text-helper">
                Recovery instructions have been sent to <strong className="text-slate-800">{email}</strong>.
              </p>
            </div>
            <Link
              to="/login"
              className="block w-full text-center bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-4 rounded-xl transition-all shadow-md text-nav"
            >
              Return to Sign In
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="email" className="text-form-label uppercase tracking-wider block">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="teacher@school.edu"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-table-body focus:ring-2 focus:ring-indigo-500/20 focus:bg-white focus:border-indigo-600 outline-none transition-all"
                required
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-4 rounded-xl transition-all shadow-md hover:shadow-lg disabled:opacity-50 text-nav flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                  <span>Sending instructions...</span>
                </>
              ) : (
                'Send Recovery Email'
              )}
            </button>

            <div className="text-center text-helper pt-4 border-t border-slate-100">
              <Link to="/login" className="text-indigo-600 hover:text-indigo-800 font-semibold transition-colors">
                Back to Sign In
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
